import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { MobileCenteredLayout } from '@/components/MobileCenteredLayout/MobileCenteredLayout'
import { api } from '@/basedata/apiCall'
import { MAX_SELFIE_ATTEMPTS } from '@/types/liveness'
import { SelfieCaptureFeed } from '@/Pages/Selfie/components/SelfieCaptureFeed'
import { SelfieHeader } from '@/Pages/Selfie/components/SelfieHeader'
import { SelfieHelpScreen } from '@/Pages/Selfie/components/SelfieHelpScreen'
import { SimpleSelfieCapture } from '@/Pages/Selfie/components/SimpleSelfieCapture'
import { simpleSelfieCopy } from '@/Pages/Selfie/copy'
import { useSelfie } from '@/Pages/Selfie/hooks/useSelfie'
import { setOnboardingSessionId } from '@/helpers/onboardingSession'
import { getSelfieConfig } from '@/helpers/selfieConfig'
import { isPlaceholderSessionId } from '@/Pages/Selfie/helpers/isPlaceholderSessionId'
import { resolveSelfieLocationState } from '@/Pages/Selfie/types/selfieLocationState'
import '@/Pages/Selfie/SelfiePage.sass'
import { WelcomeFooter } from '@/components/WelcomeFooter/WelcomeFooter'

export function SelfiePage() {
  const location = useLocation()
  const navigate = useNavigate()

  const parsed = resolveSelfieLocationState(location.state, location.search)
  const isValid = parsed !== null

  useEffect(() => {
    if (!isValid) {
      navigate('/error', { replace: true, state: { errorType: 'generic' } })
    }
  }, [isValid, navigate])

  if (!parsed) {
    return null
  }

  return <SelfiePageContent sessionId={parsed.sessionId} />
}

type ContentProps = { sessionId: string }

type SessionState = 'checking' | 'ready' | 'failed'

function SelfiePageContent({ sessionId }: ContentProps) {
  const { mockLiveness, captureType } = getSelfieConfig()
  // A sessão do ONBOARDING (aceite de consentimento) é a chave do funil e NUNCA
  // é substituída aqui. A sessão AWS Face Liveness é outra coisa: criada só no
  // modo liveness real e usada apenas para o desafio/validação na AWS —
  // misturar as duas fazia o submit/signup saírem com um id sem consentimento.
  const [livenessSessionId, setLivenessSessionId] = useState<string | null>(null)
  const [sessionState, setSessionState] = useState<SessionState>(() =>
    captureType === 'simple' || mockLiveness ? 'ready' : 'checking',
  )
  const selfie = useSelfie({
    sessionId,
    livenessSessionId,
    onLivenessSessionIdChange: setLivenessSessionId,
  })
  const { phase } = selfie

  const showLivenessFeed =
    captureType !== 'simple' &&
    sessionState === 'ready' &&
    (selfie.isTearingDown ||
      phase.phase === 'capturing' ||
      phase.phase === 'processing')

  const resolveSession = useCallback((): void => {
    if (captureType === 'simple' || mockLiveness) {
      setSessionState('ready')
      return
    }

    setSessionState('checking')

    // sessionId do onboarding vai no corpo só para o BFF conseguir amarrar uma
    // falha de criação de sessão ao convidado na modal de logs do painel.
    void api.selfie
      .createSession({ sessionId })
      .then(({ sessionId: newLivenessSessionId }) => {
        if (isPlaceholderSessionId(newLivenessSessionId)) {
          setSessionState('failed')
          return
        }

        setLivenessSessionId(newLivenessSessionId)
        setSessionState('ready')
      })
      .catch(() => {
        setSessionState('failed')
      })
  }, [captureType, mockLiveness, sessionId])

  useEffect(() => {
    if (captureType === 'simple' || mockLiveness) return
    if (livenessSessionId !== null) {
      setSessionState('ready')
      return
    }
    resolveSession()
  }, [livenessSessionId, resolveSession, mockLiveness, captureType])

  useEffect(() => {
    // Persiste a sessão do ONBOARDING — os próximos passos (signup/profile) leem
    // do storage. Nunca gravar o id da sessão AWS aqui.
    if (!isPlaceholderSessionId(sessionId)) {
      setOnboardingSessionId(sessionId)
    }
  }, [sessionId])

  return (
    <MobileCenteredLayout>
      <Box component="main" className="selfie-page" data-testid="selfie-page-main">
        {sessionState === 'checking' && (
          <Box className="selfie-page__loading-overlay" data-testid="selfie-session-loading">
            <CircularProgress />
          </Box>
        )}

        {sessionState === 'ready' && phase.phase === 'processing' && (
          <Box className="selfie-page__loading-overlay" data-testid="selfie-loading-overlay">
            <CircularProgress />
          </Box>
        )}

        {captureType === 'simple' && sessionState === 'ready' && phase.phase === 'capturing' && (
          <>
            <SelfieHeader
              canGoBack={true}
              onBack={selfie.handleCancel}
              title={simpleSelfieCopy.title}
              description={simpleSelfieCopy.description}
            />
            <SimpleSelfieCapture
              onAnalysisComplete={selfie.handleAnalysisComplete}
              onCameraRestart={selfie.handleCameraRestart}
            />
          </>
        )}

        {showLivenessFeed && (
          <>
            {phase.phase === 'capturing' && (
              <SelfieHeader
                canGoBack={!phase.cameraReady}
                onBack={selfie.handleCancel}
              />
            )}
            <SelfieCaptureFeed
              sessionId={livenessSessionId ?? sessionId}
              cameraReady={
                phase.phase === 'processing' ||
                (phase.phase === 'capturing' && phase.cameraReady)
              }
              onCameraReady={selfie.handleCameraReady}
              onAnalysisComplete={selfie.handleAnalysisComplete}
              onError={selfie.handleError}
              onCancel={selfie.handleCancel}
              onCameraRestart={selfie.handleCameraRestart}
            />
          </>
        )}

        {captureType !== 'simple' && sessionState === 'failed' && (
          <SelfieHelpScreen
            errorType="generic_liveness"
            attempts={0}
            maxAttempts={MAX_SELFIE_ATTEMPTS}
            onRetry={resolveSession}
            onContactSupport={selfie.handleContactSupport}
          />
        )}

        {captureType !== 'simple' && sessionState === 'ready' && phase.phase === 'help' && (
          <SelfieHelpScreen
            errorType={phase.errorType}
            attempts={phase.attempts}
            maxAttempts={MAX_SELFIE_ATTEMPTS}
            onRetry={selfie.handleRetry}
            onContactSupport={selfie.handleContactSupport}
          />
        )}
      </Box>
      <WelcomeFooter/>
    </MobileCenteredLayout>
  )
}
